import jsPDF from 'jspdf';

// ─── THEME COLORS (Aggressive High Contrast for White Background) ───────────
const C = {
  bg:          [255, 255, 255],  // White
  panel:       [242, 245, 248],  // Very light gray
  border:      [180, 195, 210],  // Muted steel border
  cyan:        [0, 115, 170],    // Deep cyan (readable on white)
  red:         [195, 30, 40],    // High-contrast red
  green:       [35, 130, 70],    // Forest green
  textMain:    [15, 25, 40],     // Near black
  textDim:     [90, 105, 125],   // Slate gray
  blueHeader:  [0, 60, 120],     // Dark professional blue
};

// ─── HELPERS ──────────────────────────────────────────────────────────────────
const setFill  = (doc, rgb) => doc.setFillColor  (rgb[0], rgb[1], rgb[2]);
const setStroke= (doc, rgb) => doc.setDrawColor  (rgb[0], rgb[1], rgb[2]);
const setTxt   = (doc, rgb) => doc.setTextColor  (rgb[0], rgb[1], rgb[2]);

const rect = (doc, x, y, w, h, color) => {
  setFill(doc, color);
  doc.rect(x, y, w, h, 'F');
};

const panel = (doc, x, y, w, h, borderColor = C.border) => {
  rect(doc, x, y, w, h, C.panel);
  setStroke(doc, borderColor);
  doc.setLineWidth(0.3);
  doc.rect(x, y, w, h, 'S');
};

const label = (doc, text, x, y, size, color, align = 'left') => {
  doc.setFontSize(size);
  setTxt(doc, color);
  doc.text(text, x, y, { align });
};

const hLine = (doc, x, y, w, color) => {
  setStroke(doc, color);
  doc.setLineWidth(0.2);
  doc.line(x, y, x + w, y);
};

// ─── MAIN EXPORT FUNCTION ─────────────────────────────────────────────────────
export const exportScanToPdf = ({ scan, intel, rawAllPois, identifyIntel }) => {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const W = 210;   // A4 width
  const MARGIN = 12;
  const CONTENT_W = W - MARGIN * 2;
  let y = 0;

  // ── PAGE 1: HEADER + METRICS + CHARTS ────────────────────────────────────

  // Background
  rect(doc, 0, 0, W, 297, C.bg);

  // Top header stripe
  rect(doc, 0, 0, W, 18, C.blueHeader);
  hLine(doc, 0, 18, W, C.cyan);

  // Mission badge top-left
  label(doc, '[ TACTICAL INTELLIGENCE REPORT ]', MARGIN, 8, 7, [200, 220, 240]);
  label(doc, `MISSION ID: ${scan._id.slice(-8).toUpperCase()}`, MARGIN, 13.5, 7, [255, 255, 255]);

  // Date top-right
  label(doc, new Date(scan.scanAt).toLocaleString(), W - MARGIN, 8, 7, [200, 220, 240], 'right');
  label(doc, `GEOLOC: ${scan.center.coordinates[1].toFixed(5)}, ${scan.center.coordinates[0].toFixed(5)}`, W - MARGIN, 13.5, 7, [255, 255, 255], 'right');

  y = 28;

  // ── TITLE BLOCK ──
  label(doc, 'STRATEGIC AREA SCAN', MARGIN, y, 18, C.blueHeader);
  y += 6;
  label(doc, scan.referenceId?.name?.toUpperCase() || 'GEOCOORD-LOC', MARGIN, y, 10, C.textMain);
  y += 5;
  label(doc, `PERIMETER RADIUS: ${scan.radius}m`, MARGIN, y, 9, C.textDim);
  hLine(doc, MARGIN, y + 3, CONTENT_W, C.border);
  y += 10;

  // ── METRICS ROW ──
  const totals = rawAllPois.reduce((acc, p) => {
    const { isComp, isEng } = identifyIntel(p);
    if (isComp) acc.comp++;
    if (isEng)  acc.eng++;
    return acc;
  }, { comp: 0, eng: 0 });
  const neutral = rawAllPois.length - totals.comp - totals.eng;

  const metrics = [
    { label: 'TOTAL ENTITIES', value: rawAllPois.length, color: C.blueHeader },
    { label: 'ENEMY COMPETITION', value: totals.comp, color: C.red },
    { label: 'STRATEGIC ENGINES', value: totals.eng, color: C.green },
    { label: 'NEUTRAL POI', value: neutral, color: C.textDim },
  ];

  const metW = CONTENT_W / 4 - 3;
  metrics.forEach((m, i) => {
    const mx = MARGIN + i * (metW + 4);
    panel(doc, mx, y, metW, 22, m.color);
    label(doc, String(m.value), mx + metW / 2, y + 11, 16, m.color, 'center');
    label(doc, m.label, mx + metW / 2, y + 18, 5.5, [100, 110, 120], 'center');
  });
  y += 28;

  // ── CATEGORICAL MIX ──
  const catGroups = rawAllPois.reduce((acc, p) => {
    acc[p.category] = (acc[p.category] || 0) + 1;
    return acc;
  }, {});
  const catPalette = [C.cyan, C.red, C.green, [255,140,0], [101,84,192], [0,131,212]];

  panel(doc, MARGIN, y, CONTENT_W, 6 + Object.keys(catGroups).length * 8 + 4, C.border);
  y += 5;
  label(doc, 'CATEGORICAL MIX', MARGIN + 4, y, 8, C.blueHeader);
  hLine(doc, MARGIN + 4, y + 2, CONTENT_W - 8, C.border);
  y += 8;

  const catEntries = Object.entries(catGroups).sort((a, b) => b[1] - a[1]);
  const maxCat = catEntries[0]?.[1] || 1;
  const barMaxW = CONTENT_W - 75;

  catEntries.forEach(([cat, count], i) => {
    const color = catPalette[i % catPalette.length];
    const bw = (count / maxCat) * barMaxW;
    label(doc, cat.toUpperCase(), MARGIN + 4, y + 3.5, 7, C.textMain);
    rect(doc, MARGIN + 45, y, bw, 5, color);
    rect(doc, MARGIN + 45 + bw, y, barMaxW - bw, 5, [240, 242, 245]); // Light gray bar background
    label(doc, String(count), MARGIN + 45 + barMaxW + 3, y + 3.8, 7, color);
    y += 8;
  });
  y += 8;

  // ── INTEL BALANCE ──
  panel(doc, MARGIN, y, CONTENT_W, 23, C.border);
  y += 5;
  label(doc, 'INTEL BALANCE (PROBABILITY MAP)', MARGIN + 4, y, 8, C.blueHeader);
  hLine(doc, MARGIN + 4, y + 2, CONTENT_W - 8, C.border);
  y += 8;

  const intelData = [
    { name: 'ENEMY COMPETITION', value: totals.comp, color: C.red },
    { name: 'STRATEGIC ENGINES',  value: totals.eng, color: C.green },
    { name: 'NEUTRAL POI',         value: neutral,    color: C.blueHeader },
  ];
  const total = rawAllPois.length || 1;
  const segW = CONTENT_W - 8;
  let segX = MARGIN + 4;
  intelData.forEach(seg => {
    const sw = (seg.value / total) * segW;
    if (sw > 0) {
      rect(doc, segX, y, sw - 0.5, 5, seg.color);
      segX += sw;
    }
  });
  y += 8;
  // Legend
  let lx = MARGIN + 4;
  intelData.forEach(seg => {
    rect(doc, lx, y, 4, 3, seg.color);
    label(doc, `${seg.name} (${seg.value})`, lx + 5.5, y + 2.8, 6, C.textDim);
    lx += 60;
  });
  y += 12;

  // ─── PAGE 2 (or continue): DETAILED ENTITY RECONNAISSANCE ────────────────
  doc.addPage();
  rect(doc, 0, 0, W, 297, C.bg);
  rect(doc, 0, 0, W, 18, C.blueHeader);
  hLine(doc, 0, 18, W, C.cyan);
  label(doc, '[ DETAILED ENTITY RECONNAISSANCE ]', MARGIN, 8, 7, [200, 220, 240]);
  label(doc, `MISSION ID: ${scan._id.slice(-8).toUpperCase()}  —  TOTAL POIS: ${rawAllPois.length}`, MARGIN, 13.5, 7, [255, 255, 255]);
  label(doc, new Date(scan.scanAt).toLocaleString(), W - MARGIN, 13.5, 7, [200, 220, 240], 'right');

  y = 25;

  // Group pois by category
  const grouped = rawAllPois
    .slice()
    .sort((a, b) => a.distance - b.distance)
    .reduce((acc, p) => {
      const k = p.category || 'others';
      if (!acc[k]) acc[k] = [];
      acc[k].push(p);
      return acc;
    }, {});

  const COL_NAME  = MARGIN + 2;
  const COL_INTEL = MARGIN + 105;
  const COL_DIST  = W - MARGIN - 2;
  const ROW_H     = 7;

  Object.entries(grouped).forEach(([category, pois]) => {
    // Check if we need a new page
    if (y > 260) {
      doc.addPage();
      rect(doc, 0, 0, W, 297, C.bg);
      y = 15;
    }

    // Category header
    rect(doc, MARGIN, y, CONTENT_W, 8, C.panel);
    setStroke(doc, C.border);
    doc.setLineWidth(0.4);
    doc.rect(MARGIN, y, CONTENT_W, 8, 'S');
    label(doc, `${category.toUpperCase()}  (${pois.length} entities)`, MARGIN + 3, y + 5.5, 8.5, C.blueHeader);
    y += 12;

    // Column headers
    label(doc, 'ENTITY NAME',  COL_NAME,  y, 6, [100, 110, 130]);
    label(doc, 'INTEL CLASS',  COL_INTEL, y, 6, [100, 110, 130]);
    label(doc, 'DISTANCE',     COL_DIST,  y, 6, [100, 110, 130], 'right');
    hLine(doc, MARGIN, y + 1.5, CONTENT_W, C.border);
    y += 6;

    pois.forEach((p, idx) => {
      if (y > 280) {
        doc.addPage();
        rect(doc, 0, 0, W, 297, C.bg);
        y = 15;
      }

      const { isComp, isEng } = identifyIntel(p);
      const intelLabel = isComp ? 'ENEMY COMPETITION' : isEng ? 'STRATEGIC ENGINE' : 'NEUTRAL';
      const intelColor = isComp ? C.red : isEng ? C.green : [120, 135, 150];

      // Row alternating bg
      if (idx % 2 === 1) {
        rect(doc, MARGIN, y - 2, CONTENT_W, ROW_H, [248, 250, 253]);
      }

      // Name (truncated)
      const nameStr = p.name.length > 55 ? p.name.slice(0, 53) + '…' : p.name;
      label(doc, nameStr,             COL_NAME,  y + 2.8, 7.5, C.textMain);
      label(doc, intelLabel,          COL_INTEL, y + 2.8, 6.5, intelColor);
      label(doc, `${p.distance}m`,    COL_DIST,  y + 2.8, 7.5, C.blueHeader, 'right');

      hLine(doc, MARGIN, y + 4.5, CONTENT_W, [242, 244, 247]);
      y += ROW_H;
    });

    y += 6;
  });

  // ── FOOTER ──
  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    rect(doc, 0, 288, W, 9, C.panel);
    hLine(doc, 0, 288, W, C.border);
    label(doc, `[SYSTEM_LOG] Scan Reference: ${new Date(scan.scanAt).toISOString()} | GEOLOC: ${scan.center.coordinates[1].toFixed(6)}, ${scan.center.coordinates[0].toFixed(6)}`, MARGIN, 293.5, 6, C.textDim);
    label(doc, `PAGE ${i} / ${totalPages}`, W - MARGIN, 293.5, 6, C.textDim, 'right');
  }

  const filename = `SCAN_${scan._id.slice(-8).toUpperCase()}_${new Date(scan.scanAt).toLocaleDateString('en-US', { year:'2-digit', month:'2-digit', day:'2-digit' }).replace(/\//g,'-')}.pdf`;
  doc.save(filename);
};
